import React, { FC } from "react";
import usePagination from "../../../../hooks/usePagination";
import { useSearchFilter } from "../../../../hooks/useSearchFilter";
import { useProducts } from "../../hooks/useProducts";
import { Product } from "../../models/Product";
import {
  Table,
  Column,
} from "../../../../components/reusable-components/table/Table";
import ListHeader from "../../../../components/reusable-components/table/ListHeader";

interface Props {
  categoryId: string;
}

const CategoryProductsList: FC<Props> = ({ categoryId }) => {
  const pagination = usePagination<keyof Product>({
    initialPage: 1,
    initialSortBy: "id",
    initialSortDir: "desc",
  });

  const { currentPage, goToPage } = pagination;

  const { search, debouncedSearch, handleSearchChange } = useSearchFilter({
    onDebounce: () => goToPage(1),
  });

  const {
    fetch,
    pagination: { total },
  } = useProducts({
    params: {
      ...pagination.query,
      search: debouncedSearch,
      category_id: categoryId,
    },
  });

  const { data: products, loading, error } = fetch;

  const columns: Column<Product>[] = [
    { field: "id", headerName: "ID", sortable: true },
    { field: "name", headerName: "Name", sortable: true },
    { field: "slug", headerName: "Slug" },
    { field: "stock_quantity", headerName: "Stock", sortable: true },
    { field: "brand_name", headerName: "Brand" },
  ];

  return (
    <div>
      <ListHeader
        title="Products in This Category"
        search={{
          value: search,
          onChange: handleSearchChange,
          placeholder: "Search by product name...",
          loading,
          disabled: loading,
        }}
      />

      <Table<Product>
        columns={columns}
        data={products}
        loading={loading}
        error={error}
        sortBy={pagination.sortBy}
        sortDir={pagination.sortDir}
        onSortChange={pagination.handleSortChange}
        pagination={{
          page: currentPage - 1,
          rowsPerPage: pagination.query.per_page ?? 10,
          count: total,
          onPageChange: (_, newPage) => goToPage(newPage + 1),
          onRowsPerPageChange: () => {},
        }}
      />
    </div>
  );
};

export default CategoryProductsList;
